import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Users, TrendingUp, DollarSign, BarChart3, Target, ShoppingCart, Settings, Brain, PieChart, ChevronDown, ChevronUp, Search, Map, Zap, Download, X, Loader2, Package } from 'lucide-react';
import { BedrockAgentRuntimeClient, InvokeAgentCommand } from "@aws-sdk/client-bedrock-agent-runtime";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChartWithControls from '../chatbotui/chartwithcontrols';
import DataTable from './datatable';
import SQLValidator from '../../utils/SQLValidator';
import { buildSQLPrompt } from '../../utils/prompts';
import config from '../../amplifyconfiguration.json';

const GuiaInteractiva = () => {
    const [activeTab, setActiveTab] = useState('fases');
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [expandedSections, setExpandedSections] = useState(new Set([0]));

    // Estados para el modal y agente
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agentResponse, setAgentResponse] = useState('');
    const [vizLoading, setVizLoading] = useState(false);
    const [queryData, setQueryData] = useState(null);
    const [currentMessageId, setCurrentMessageId] = useState(null);

    // Estado para el sistema de cache
    const [queryCache, setQueryCache] = useState({});

    // Usuario y configuración
    const { user } = useAuthenticator((context) => [context.user]);
    const id_user = user?.username || 'demo_user';
    const id_conversation = `guia_interactiva_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // URLs y configuración
    const baseURL = process.env.REACT_APP_BASE_URL;
    const baseURLBD = process.env.REACT_APP_BD;
    const AGENT_ID = "V1U172A3JU";
    const AGENT_ALIAS_ID = "GV49A1TG6P";

    // Endpoints
    const endpoint_add_message = '/add_message';
    const endpoint_get_sql = '/get_query';
    const endpoint_executeSQL = '/get_rds';

    // Funciones de cache
    const generateCacheKey = (query) => {
        return query.toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
    };

    const saveToCache = (query, data) => {
        const cacheKey = generateCacheKey(query);
        setQueryCache(prev => ({
            ...prev,
            [cacheKey]: {
                ...data,
                timestamp: Date.now(),
                query: query
            }
        }));
    };

    const getFromCache = (query) => {
        const cacheKey = generateCacheKey(query);
        return queryCache[cacheKey];
    };

    const existsInCache = (query) => {
        const cacheKey = generateCacheKey(query);
        return !!queryCache[cacheKey];
    };

    // Iconos para roles y fases
    const roleIcons = {
        "CEO": Brain,
        "CFO": DollarSign,
        "Director Comercial": TrendingUp,
        "Director de Revenue Management": BarChart3,
        "Director de Marketing": Target,
        "Gerente de Pricing": Settings,
        "Analista de Precios": PieChart,
        "Gerente de Categorías": ShoppingCart
    };

    const faseIcons = {
        "Exploración de Datos (EDA)": Search,
        "Análisis Descriptivo Pricing": BarChart3,
        "Analítica Comparativa vs Competencia": Brain,
        "Análisis de Rentabilidad & Márgenes": DollarSign,
        "Análisis de Sell-Out por Canal": Package,
        "Optimización de Precios": Zap,
        "Exportación de Recomendaciones": Download
    };

    // ============= CONSULTAS ADAPTADAS CON TABLA SELL_OUT =============

    const consultasPorRol = [
        {
            title: "CEO",
            description: "Visión estratégica global de pricing, competitividad y participación de mercado",
            priority: "Alta",
            timeEstimate: "15-20 min",
            consultas: [
                "¿Cuáles son los top 10 productos con mayor participación de mercado (market share) en YTD?",
                "Comparativo de nuestros precios vs. competencia: ¿en qué categorías somos más competitivos?",
                "Análisis de tendencia de ventas YTD vs MTD por departamento",
                "¿Cuáles son los top 5 clientes/canales con mayor sell-out en valor durante el último año?",
                "Análisis de velocidad de rotación: comparar sell-in (client) vs sell-out por producto"
            ]
        },
        {
            title: "CFO",
            description: "Análisis financiero, rentabilidad y optimización de márgenes",
            priority: "Alta",
            timeEstimate: "18-25 min",
            consultas: [
                "¿Qué productos tienen margen actual por debajo del margen mínimo aceptable?",
                "Análisis de rentabilidad: productos con mejor margen y mayor volumen de ventas YTD",
                "¿Cuál es el impacto financiero de ajustar precios al nivel de la competencia por categoría?",
                "Análisis de rentabilidad por canal: ¿qué clientes generan mayor valor en sell-out?",
                "Productos con desbalance entre margen e inventario vs sell-out efectivo por cliente"
            ]
        },
        {
            title: "Director Comercial",
            description: "Estrategias de ventas y penetración de mercado por canal",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                "¿Cuáles son los productos con mayor crecimiento en ventas MTD vs mismo período anterior?",
                "Análisis de performance de ventas por categoría: volumen vs valor",
                "¿Qué productos tienen alto inventario pero bajas ventas MTD?",
                "Ranking de clientes por volumen de sell-out: ¿quiénes son nuestros mejores distribuidores?",
                "Análisis de cobertura de mercado: ¿en qué mercados tenemos mayor penetración de sell-out?",
                "Comparativo sell-in vs sell-out por cliente: identificar cuellos de botella en distribución"
            ]
        },
        {
            title: "Director de Revenue Management",
            description: "Optimización de ingresos con analytics avanzados y gestión de canal",
            priority: "Alta",
            timeEstimate: "22-28 min",
            consultas: [
                "Optimización de precios: productos con oportunidad de incremento sin perder competitividad",
                "¿Cuál es la correlación entre precio con IVA y unidades vendidas por subcategoría?",
                "Análisis de elasticidad: productos donde cambios en precio impactan más en volumen",
                "Tendencias mensuales de sell-out: ¿qué meses tienen mejor performance por producto?",
                "Análisis de eficiencia de canal: valor promedio de sell-out por unidad vs precio con IVA",
                "Identificar productos con alta rotación en sell-out pero bajo margen para optimización"
            ]
        },
        {
            title: "Director de Marketing",
            description: "Estrategias de marketing, posicionamiento y promociones",
            priority: "Media",
            timeEstimate: "15-20 min",
            consultas: [
                "¿Qué competidores tienen promociones activas (Sales Flag) en nuestras categorías clave?",
                "Análisis de brechas de precio: productos donde estamos significativamente más caros o baratos",
                "¿Cómo varían los precios de competencia por canal para nuestros productos estrella?",
                "Productos con mayor sell-out en mercados específicos: oportunidades de expansión regional"
            ]
        },
        {
            title: "Gerente de Pricing",
            description: "Gestión operativa de precios y análisis competitivo",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                "Comparativo detallado precio con IVA vs precio final de competidores por producto",
                "¿Qué productos están fuera de rango competitivo (±10%) respecto al mercado?",
                "Análisis de descuentos: diferencia entre precio sin promoción y precio actual por marca",
                "Análisis de precio realizado: valor promedio de sell-out vs precio de lista por producto"
            ]
        },
        {
            title: "Analista de Precios",
            description: "Análisis operativo profundo y monitoreo continuo",
            priority: "Media",
            timeEstimate: "25-30 min",
            consultas: [
                "Análisis granular de precios por canal de competencia: identificar canales más agresivos",
                "¿Cuál es la profundidad promedio de descuento de competidores por categoría?",
                "Monitoreo de cambios de precio: competidores con mayor variación en last_price vs price",
                "Análisis histórico de sell-out: tendencias año a año por producto y cliente"
            ]
        },
        {
            title: "Gerente de Categorías",
            description: "Optimización por categorías y gestión de portafolio con datos de sell-out",
            priority: "Media",
            timeEstimate: "18-22 min",
            consultas: [
                "Performance por categoría: ventas YTD, participación de mercado y margen promedio",
                "¿Qué subcategorías tienen productos con alto stock pero baja rotación?",
                "Análisis de mix de productos: identificar oportunidades de cross-selling por categoría",
                "Productos estrella por cliente: ¿qué SKUs tienen mejor sell-out en cada canal?",
                "Análisis de concentración: ¿qué porcentaje del sell-out proviene del top 20% de productos?",
                "Comparativo de performance: productos con mejor sell-out vs productos con mayor inventario"
            ]
        }
    ];

    const consultasPorFase = [
        {
            title: "Exploración de Datos (EDA)",
            description: "Análisis exploratorio de catálogo, precios, ventas y sell-out",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                {
                    text: "Dame un resumen del catálogo: total de productos por departamento y categoría",
                },
                {
                    text: "¿Cuál es la distribución de precios (con IVA) por categoría?",
                },
                {
                    text: "Muéstrame los top 10 productos con mayor inventario disponible",
                },
                {
                    text: "¿Qué marcas tienen mayor cantidad de SKUs activos en el catálogo?",
                },
                {
                    text: "Análisis de ventas: productos con mayor volumen en piezas YTD",
                },
                {
                    text: "Resumen de sell-out: total de clientes/canales activos y volumen total de ventas",
                },
                {
                    text: "¿Cuáles son los mercados con mayor actividad de sell-out?",
                }
            ]
        },
        {
            title: "Análisis Descriptivo Pricing",
            description: "Consultas enfocadas en estrategias de precio",
            priority: "Alta",
            timeEstimate: "18-22 min",
            consultas: [
                {
                    text: "¿Cuál es el precio promedio con IVA por categoría y subcategoría?",
                },
                {
                    text: "Comparativo: precio sin promoción vs precio actual por marca",
                },
                {
                    text: "¿Qué productos tienen mayor diferencia porcentual entre precio sin promoción y precio con IVA?",
                },
                {
                    text: "Análisis de PMP (Precio Medio Ponderado): productos con PMP superior al precio con IVA",
                },
                {
                    text: "Precio promedio realizado: comparar precio con IVA vs valor promedio de sell-out por unidad",
                }
            ]
        },
        {
            title: "Analítica Comparativa vs Competencia",
            description: "Análisis de competitividad usando datos de match y competidores",
            priority: "Alta",
            timeEstimate: "25-30 min",
            consultas: [
                {
                    text: "¿Cómo se comparan nuestros precios vs precios finales de competidores por canal?",
                },
                {
                    text: "¿Qué competidores tienen promociones activas (Sales Flag) en mis categorías?",
                },
                {
                    text: "Análisis de brechas de precio: productos donde estamos más caros o baratos que competencia",
                },
                {
                    text: "¿Cuál es la profundidad de descuento promedio de competidores (Price vs Final Price) por categoría?",
                }
            ]
        },
        {
            title: "Análisis de Rentabilidad & Márgenes",
            description: "Métricas de rentabilidad y optimización financiera",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                {
                    text: "¿Qué productos tienen margen actual por debajo del margen mínimo?",
                },
                {
                    text: "Top 20 productos con mejor balance entre margen y volumen de ventas YTD",
                },
                {
                    text: "Análisis de rentabilidad por categoría: margen promedio vs ventas totales YTD",
                },
                {
                    text: "¿Qué productos tienen bajo margen pero alta rotación (ventas MTD > promedio)?",
                },
                {
                    text: "Rentabilidad por canal: margen promedio vs volumen de sell-out por cliente",
                }
            ]
        },
        {
            title: "Análisis de Sell-Out por Canal",
            description: "Análisis detallado de ventas efectivas a través de distribuidores y canales",
            priority: "Alta",
            timeEstimate: "25-30 min",
            consultas: [
                {
                    text: "Ranking de clientes por volumen de sell-out en unidades y valor durante el último año",
                },
                {
                    text: "Análisis de concentración: ¿qué porcentaje del sell-out proviene del top 20% de clientes?",
                },
                {
                    text: "Tendencia mensual de sell-out: ¿cuáles son los meses con mejor performance?",
                },
                {
                    text: "Productos con mayor sell-out por cliente: identificar SKUs estrella en cada canal",
                },
                {
                    text: "Análisis de mercados: ¿en qué regiones tenemos mayor penetración de sell-out?",
                },
                {
                    text: "Comparativo sell-in vs sell-out: productos con desbalance entre inventario enviado y ventas efectivas",
                },
                {
                    text: "Velocidad de rotación en canal: tiempo promedio entre sell-in y sell-out por producto",
                },
                {
                    text: "Análisis de estacionalidad: variación año a año del sell-out por mes y producto",
                }
            ]
        },
        {
            title: "Optimización de Precios",
            description: "Recomendaciones inteligentes de pricing con datos de sell-out",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                {
                    text: "Productos con oportunidad de incremento de precio sin perder competitividad",
                },
                {
                    text: "¿Cuál es la elasticidad precio-demanda por subcategoría? (correlación precio vs ventas piezas)",
                },
                {
                    text: "Estrategia de pricing dinámico: ajustes recomendados considerando competencia y margen",
                },
                {
                    text: "Optimización de portafolio: productos para ajustar precio basado en participación de mercado",
                },
                {
                    text: "Productos con alto sell-out pero bajo margen: oportunidades de optimización de precio",
                },
                {
                    text: "Análisis de precio realizado: productos donde el valor de sell-out difiere significativamente del precio de lista",
                }
            ]
        },
        {
            title: "Exportación de Recomendaciones",
            description: "Reportes ejecutivos y dashboards de métricas clave incluyendo sell-out",
            priority: "Media",
            timeEstimate: "10-15 min",
            consultas: [
                {
                    text: "Reporte ejecutivo: top productos para optimización de precio con datos de competencia",
                },
                {
                    text: "Dashboard de KPIs: ventas YTD/MTD, market share, margen y competitividad por categoría",
                },
                {
                    text: "Matriz de pricing: clasificación de productos por competitividad y rentabilidad",
                },
                {
                    text: "Recomendaciones accionables: productos para promocionar, ajustar precio o descontinuar",
                },
                {
                    text: "Reporte de sell-out: performance de clientes/canales con métricas de volumen, valor y crecimiento",
                },
                {
                    text: "Dashboard de distribución: comparativo sell-in vs sell-out por cliente y producto",
                }
            ]
        }
    ];

    // ============= FUNCIONES DEL AGENTE (SIN CAMBIOS) =============

    const createAuthenticatedRequest = async (config) => {
        const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
        const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;

        const credentials = fromCognitoIdentityPool({
            clientConfig: { region: config.aws_project_region },
            identityPoolId: config.aws_cognito_identity_pool_id,
            logins: { [my_login]: authToken }
        });

        const signer = new SignatureV4({
            credentials: credentials,
            region: config.aws_project_region,
            service: 'execute-api',
            sha256: Sha256
        });

        return async (endpoint, method = 'GET', body = null) => {
            const url = new URL(endpoint);

            const request = new HttpRequest({
                hostname: url.hostname,
                path: url.pathname + url.search,
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    host: url.hostname
                },
                body: body ? JSON.stringify(body) : undefined
            });

            const signedRequest = await signer.sign(request);
            const fetchOptions = {
                method: signedRequest.method,
                headers: signedRequest.headers,
                body: signedRequest.body
            };

            const response = await fetch(url.toString(), fetchOptions);
            return response.json();
        };
    };

    const invokeBedrockAgent = async (prompt, sessionId, id_message_bot) => {
        const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
        const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;

        const bedrock = new BedrockAgentRuntimeClient({
            region: config.aws_project_region,
            credentials: fromCognitoIdentityPool({
                clientConfig: { region: config.aws_project_region },
                identityPoolId: config.aws_cognito_identity_pool_id,
                logins: { [my_login]: authToken }
            })
        });

        const input = {
            agentId: AGENT_ID,
            agentAliasId: AGENT_ALIAS_ID,
            sessionId,
            inputText: prompt,
            enableTrace: true,
            sessionState: {
                sessionAttributes: {
                    id_message: id_message_bot
                }
            }
        };

        const command = new InvokeAgentCommand(input);
        let completion = "";

        const response = await bedrock.send(command);
        if (response.completion === undefined) {
            throw new Error("Completion is undefined");
        }

        for await (let chunkEvent of response.completion) {
            if (chunkEvent.chunk) {
                const chunk = chunkEvent.chunk;
                const decodedResponse = new TextDecoder("utf-8").decode(chunk.bytes);
                completion += decodedResponse;
            }
        }

        return completion;
    };

    const addMessageBD = async (id_conversation, id_user, message, author, id_message) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}${endpoint_add_message}`;

            const response = await authenticatedRequest(
                url,
                'POST',
                {
                    id_user: id_user,
                    id_conversation: id_conversation,
                    content: message,
                    author: author,
                    id_message: id_message
                }
            );

            return response.id_message;
        } catch (error) {
            console.error('Error adding message to BD:', error);
            return "None";
        }
    };

    const getQuery = async (id_message) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}${endpoint_get_sql}?id_message=${encodeURIComponent(id_message)}`;
            const response = await authenticatedRequest(url, 'GET', null);
            return response.messages;
        } catch (error) {
            console.error('Error getting query:', error);
            return [];
        }
    };

    const sendMessageToClaude = async (mensajes, userContent) => {
        const apiKey = "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";
        const mensajes_form = mensajes.map(({ question, query }) => ({ question, query }));

        const safeJSONStringify = (obj) => {
            try {
                return JSON.stringify(obj);
            } catch (error) {
                console.warn("Error en serialización JSON:", error);
                const seen = new WeakSet();
                return JSON.stringify(obj, (key, value) => {
                    if (typeof value === 'object' && value !== null) {
                        if (seen.has(value)) {
                            return "[Referencia Circular]";
                        }
                        seen.add(value);
                    }
                    if (typeof value === 'function') {
                        return "[Función]";
                    }
                    if (value instanceof Error) {
                        return {
                            errorType: value.name,
                            errorMessage: value.message
                        };
                    }
                    return value;
                }, 2);
            }
        };

        const content = buildSQLPrompt(userContent, safeJSONStringify(mensajes_form));

        return fetch("https://api.anthropic.com/v1/messages", {
            method: "POST",
            headers: {
                "x-api-key": apiKey,
                "anthropic-version": "2023-06-01",
                "content-type": "application/json",
                "anthropic-dangerous-direct-browser-access": "true"
            },
            body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 1000,
                messages: [{
                    role: "user",
                    content: content
                }]
            })
        })
            .then(response => {
                if (!response.ok) {
                    console.error('Error en la API de Claude:', response.statusText);
                    return null;
                }
                return response.json();
            })
            .catch(error => {
                console.error('Error haciendo la petición:', error);
                return null;
            });
    };

    const executeSQL = async (sqlStatement) => {
        try {
            const url = `${baseURLBD}${endpoint_executeSQL}?sqlStatement=${encodeURIComponent(sqlStatement)}`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error executing SQL query:', error);
            let errorMessage = "Error al ejecutar la consulta SQL";

            if (error.name === 'AbortError') {
                errorMessage = "La consulta ha excedido el tiempo máximo de ejecución";
            } else if (error.message) {
                errorMessage = error.message;
            }

            return {
                data: [],
                columns: {},
                query: sqlStatement,
                error: errorMessage
            };
        }
    };

    const updateMessageBD = async (id_message, query_sql) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}/update_query_sql`;

            const response = await authenticatedRequest(
                url,
                'POST',
                {
                    id_message: id_message,
                    query_sql: query_sql
                }
            );

            return response.message;
        } catch (error) {
            console.error('Error updating query_sql:', error);
            return "Failed to update query_sql";
        }
    };

    const handleGenerateExcel = () => {
        if (!queryData || !queryData.data) {
            console.error('No hay datos disponibles para generar el Excel.');
            return;
        }

        const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '');
        const worksheet = XLSX.utils.json_to_sheet(queryData.data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
        XLSX.writeFile(workbook, `cantabria_labs_analisis_${timestamp}.xlsx`);
    };

    const handleQueryClick = async (query) => {
        const queryText = typeof query === 'object' ? query.text : query;

        setSelectedQuery(queryText);
        setModalIsOpen(true);
        setLoading(true);
        setAgentResponse('');
        setQueryData(null);
        setVizLoading(false);

        try {
            if (existsInCache(queryText)) {
                console.log('📦 Respuesta encontrada en cache');

                const cachedData = getFromCache(queryText);
                setAgentResponse(cachedData.agentResponse);
                setCurrentMessageId(cachedData.messageId);

                if (cachedData.queryData) {
                    setQueryData(cachedData.queryData);
                }

                setLoading(false);
                return;
            }

            console.log('🔄 Consultando agente...');

            const id_message_client = uuidv4();
            const id_message_bot = uuidv4();
            setCurrentMessageId(id_message_bot);

            await addMessageBD(id_conversation, id_user, queryText, "Client", id_message_client);

            const response = await invokeBedrockAgent(queryText, id_conversation, id_message_bot);

            if (!response || response === "Sorry, I am unable to assist you with this request.") {
                throw new Error("El agente no pudo procesar la consulta");
            }

            setAgentResponse(response);

            await addMessageBD(id_conversation, id_user, response, "Bot", id_message_bot);

            saveToCache(queryText, {
                agentResponse: response,
                messageId: id_message_bot,
                queryData: null,
                sqlQuery: null
            });

        } catch (error) {
            console.error('Error processing query:', error);
            setAgentResponse("Lo siento, hubo un error al procesar tu consulta. Por favor, intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    const handleVizualization = async () => {
        if (!currentMessageId) return;

        setVizLoading(true);
        setQueryData(null);

        try {
            const cachedData = getFromCache(selectedQuery);
            if (cachedData && cachedData.queryData && cachedData.sqlQuery) {
                console.log('📊 Datos de visualización desde cache');
                setQueryData(cachedData.queryData);
                setVizLoading(false);
                return;
            }

            console.log('🔄 Generando nueva visualización...');

            const mensajes_anteriores = await getQuery(currentMessageId);

            const respuestaClaude = await sendMessageToClaude(mensajes_anteriores, selectedQuery);

            if (!respuestaClaude || !respuestaClaude.content?.[0]?.text) {
                throw new Error("No se pudo generar la consulta SQL");
            }

            let sqlQuery = respuestaClaude.content[0].text;

            const validator = new SQLValidator();
            sqlQuery = validator.cleanMarkdown(sqlQuery);

            await updateMessageBD(currentMessageId, sqlQuery);

            const records = await executeSQL(sqlQuery);

            if (records.error) {
                throw new Error(records.error);
            }

            const newQueryData = {
                data: records.data || [],
                columns: records.columns || {},
                query: sqlQuery,
                column_types: records.column_types || {},
                hasError: false
            };

            setQueryData(newQueryData);

            if (cachedData) {
                saveToCache(selectedQuery, {
                    ...cachedData,
                    queryData: newQueryData,
                    sqlQuery: sqlQuery
                });
            }

        } catch (error) {
            console.error('Error in visualization:', error);
            const errorData = {
                data: [],
                columns: {},
                query: "",
                column_types: {},
                hasError: true,
                error: error.message || "No se puede generar la gráfica. Por favor, intenta reformular tu pregunta."
            };

            setQueryData(errorData);

            const cachedData = getFromCache(selectedQuery);
            if (cachedData) {
                saveToCache(selectedQuery, {
                    ...cachedData,
                    queryData: errorData,
                    sqlQuery: null
                });
            }
        } finally {
            setVizLoading(false);
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedQuery(null);
        setAgentResponse('');
        setQueryData(null);
        setCurrentMessageId(null);
        setLoading(false);
        setVizLoading(false);
    };

    const dataToShow = activeTab === 'fases' ? consultasPorFase : consultasPorRol;
    const iconsToUse = activeTab === 'fases' ? faseIcons : roleIcons;

    const cleanMarkdownResponse = (text) => {
        if (!text) return '';

        let cleaned = text
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/\u00A0/g, ' ')
            .replace(/\u200B/g, '')
            .trim();

        const lines = cleaned.split('\n');
        const processedLines = [];
        let inTable = false;

        for (let i = 0; i < lines.length; i++) {
            let line = lines[i].trim();

            if (line.startsWith('|') && line.endsWith('|')) {
                inTable = true;
                line = line.replace(/\|/g, ' | ').replace(/\s+\|/g, ' |').replace(/\|\s+/g, '| ');
                processedLines.push(line);

                if (i + 1 < lines.length && !lines[i + 1].includes('---')) {
                    const columns = line.split('|').filter(col => col.trim());
                    const separator = '|' + columns.map(() => '---').join('|') + '|';
                    processedLines.push(separator);
                }
            } else if (inTable && !line.startsWith('|')) {
                inTable = false;
                processedLines.push(line);
            } else {
                processedLines.push(line);
            }
        }

        return processedLines.join('\n');
    };

    return (
        <div className="roadmap-container">
            {/* Tabs Header */}
            <div className="tabs-header">
                <div className="tabs-nav">
                    <button
                        className={`tab-button ${activeTab === 'fases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('fases')}
                    >
                        <Search size={20} />
                        Fases de Análisis
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => setActiveTab('roles')}
                    >
                        <Users size={20} />
                        Roadmap por Roles
                    </button>
                </div>
            </div>

            {/* Header */}
            <div className="header">
                <div className="title-section">
                    <h1>{activeTab === 'fases' ? 'Fases de Análisis IA Pricing & Competencia - Cantabria Labs' : 'Roadmap de Consultas por Roles - Pricing'}</h1>
                    <p>{activeTab === 'fases' ? 'Metodología estructurada para análisis de precios, competitividad y sell-out por canal' : 'Guía completa por roles para maximizar insights de pricing, sell-out y revenue management'}</p>
                </div>

                <div className="progress-section">
                    <div className="progress-stats">
                        <div className="stat">
                            <span className="stat-number">{completedSteps.size}</span>
                            <span className="stat-label">Completados</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">{dataToShow.length - completedSteps.size}</span>
                            <span className="stat-label">Pendientes</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">{Math.round((completedSteps.size / dataToShow.length) * 100)}%</span>
                            <span className="stat-label">Progreso</span>
                        </div>
                    </div>
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${(completedSteps.size / dataToShow.length) * 100}%` }} />
                    </div>
                </div>
            </div>

            <div className="main-content">
                {/* Timeline Roadmap */}
                <div className="timeline-container">
                    <div className="timeline-line" />

                    {dataToShow.map((section, index) => {
                        const IconComponent = iconsToUse[section.title];
                        const isCompleted = completedSteps.has(index);
                        const isExpanded = expandedSections.has(index);

                        return (
                            <div key={index} className={`timeline-item ${isCompleted ? 'completed' : ''}`}>
                                <div className="timeline-node">
                                    <IconComponent size={24} color="white" />
                                </div>

                                <div className="timeline-card">
                                    <div className="card-header">
                                        <div className="role-info">
                                            <div className="role-title-row">
                                                <h3>{section.title}</h3>
                                                <div className="badges">
                                                    <span className={`priority-badge priority-${section.priority.toLowerCase()}`}>
                                                        {section.priority}
                                                    </span>
                                                    <span className="time-badge">{section.timeEstimate}</span>
                                                </div>
                                            </div>
                                            <p className="role-description">{section.description}</p>
                                            <div className="role-stats">
                                                <span className="query-count">{section.consultas.length} consultas disponibles</span>
                                            </div>
                                        </div>

                                        <div className="card-actions">
                                            <button
                                                className={`completion-btn ${isCompleted ? 'completed' : ''}`}
                                                onClick={() => {
                                                    const newCompleted = new Set(completedSteps);
                                                    if (newCompleted.has(index)) {
                                                        newCompleted.delete(index);
                                                    } else {
                                                        newCompleted.add(index);
                                                    }
                                                    setCompletedSteps(newCompleted);
                                                }}
                                            >
                                                {isCompleted ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                                                {isCompleted ? 'Completado' : 'Pendiente'}
                                            </button>

                                            <button
                                                className="expand-btn"
                                                onClick={() => {
                                                    const newExpanded = new Set(expandedSections);
                                                    if (newExpanded.has(index)) {
                                                        newExpanded.delete(index);
                                                    } else {
                                                        newExpanded.add(index);
                                                    }
                                                    setExpandedSections(newExpanded);
                                                }}
                                            >
                                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="card-content">
                                            <h4>Consultas Recomendadas:</h4>
                                            <div className="queries-list">
                                                {section.consultas.map((consulta, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="query-item"
                                                        onClick={() => handleQueryClick(consulta)}
                                                    >
                                                        <div className="query-number">{idx + 1}</div>
                                                        <p>{activeTab === 'fases' ? consulta.text : consulta}</p>
                                                        <div className="query-arrow">→</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal de resultados */}
            {modalIsOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Consulta: {selectedQuery}</h3>
                            <button className="close-btn" onClick={closeModal}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className="modal-body">
                            {loading ? (
                                <div className="loading-container">
                                    <Loader2 className="animate-spin" size={40} />
                                    <p>Procesando consulta con IA...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="agent-response-section">
                                        <h4>💡 Respuesta del Agente</h4>
                                        <div className="agent-response-content">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    code({ node, inline, className, children, ...props }) {
                                                        const match = /language-(\w+)/.exec(className || '');
                                                        return !inline && match ? (
                                                            <SyntaxHighlighter
                                                                style={tomorrow}
                                                                language={match[1]}
                                                                PreTag="div"
                                                                {...props}
                                                            >
                                                                {String(children).replace(/\n$/, '')}
                                                            </SyntaxHighlighter>
                                                        ) : (
                                                            <code className={className} {...props}>
                                                                {children}
                                                            </code>
                                                        );
                                                    },
                                                    table({ children }) {
                                                        return (
                                                            <div className="table-wrapper">
                                                                <table className="markdown-table">
                                                                    {children}
                                                                </table>
                                                            </div>
                                                        );
                                                    },
                                                    thead({ children }) {
                                                        return <thead className="markdown-thead">{children}</thead>;
                                                    },
                                                    tbody({ children }) {
                                                        return <tbody className="markdown-tbody">{children}</tbody>;
                                                    },
                                                    tr({ children }) {
                                                        return <tr className="markdown-tr">{children}</tr>;
                                                    },
                                                    th({ children, style }) {
                                                        return (
                                                            <th className="markdown-th" style={style}>
                                                                {children}
                                                            </th>
                                                        );
                                                    },
                                                    td({ children, style }) {
                                                        return (
                                                            <td className="markdown-td" style={style}>
                                                                {children}
                                                            </td>
                                                        );
                                                    }
                                                }}
                                            >
                                                {cleanMarkdownResponse(agentResponse)}
                                            </ReactMarkdown>
                                        </div>
                                    </div>

                                    <div className="visualization-section">
                                        <button
                                            className="visualize-btn"
                                            onClick={handleVizualization}
                                            disabled={vizLoading}
                                        >
                                            {vizLoading ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={16} />
                                                    Generando visualización...
                                                </>
                                            ) : (
                                                <>
                                                    <BarChart3 size={20} />
                                                    Visualizar Datos
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {queryData && (
                                        <div className="visualization-results">
                                            {queryData.hasError ? (
                                                <div className="error-message">
                                                    <h4>⚠️ Error en la visualización</h4>
                                                    <p>{queryData.error}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="chart-container">
                                                        <h4>📊 Visualización de Datos</h4>
                                                        <ChartWithControls
                                                            columns={queryData.columns || {}}
                                                            data={queryData.data || []}
                                                            column_types={queryData.column_types || {}}
                                                            hasError={queryData.hasError || false}
                                                            error={queryData.error || null}
                                                        />

                                                        <div className="excel-download">
                                                            <button onClick={handleGenerateExcel} className="excel-btn">
                                                                <Download size={16} />
                                                                Descargar Excel
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {queryData.data && queryData.data.length > 0 && (
                                                        <div className="data-table">
                                                            <h4>📋 Vista de Datos</h4>
                                                            <DataTable
                                                                columns={queryData.columns || {}}
                                                                data={queryData.data || []}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* CSS - (sin cambios, mismo que antes) */}
            <style jsx>{`
                .roadmap-container {
                    min-height: 100vh;
                    background-color: #f8fafc;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                .tabs-header {
                    background: white;
                    border-bottom: 1px solid #e2e8f0;
                    padding: 0 2rem;
                }

                .tabs-nav {
                    max-width: 1200px;
                    margin: 0 auto;
                    display: flex;
                    gap: 0;
                }

                .tab-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem 1.5rem;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    transition: all 0.3s ease;
                    font-weight: 500;
                }

                .tab-button:hover {
                    color: #2563eb;
                    background: #f8fafc;
                }

                .tab-button.active {
                    color: #2563eb;
                    border-bottom-color: #2563eb;
                    background: #f8fafc;
                }

                .main-content {
                    padding: 2rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .header {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e2e8f0;
                    margin-bottom: 2rem;
                }

                .title-section h1 {
                    color: #1e293b;
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin: 0 0 0.5rem 0;
                }

                .title-section p {
                    color: #64748b;
                    font-size: 1.1rem;
                    margin: 0;
                }

                .progress-section {
                    display: flex;
                    align-items: center;
                    gap: 2rem;
                    margin-top: 2rem;
                }

                .progress-stats {
                    display: flex;
                    gap: 2rem;
                }

                .stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .stat-number {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #2563eb;
                }

                .stat-label {
                    font-size: 0.9rem;
                    color: #64748b;
                    text-transform: uppercase;
                    font-weight: 500;
                }

                .progress-bar {
                    flex: 1;
                    height: 8px;
                    background: #e2e8f0;
                    border-radius: 4px;
                    overflow: hidden;
                }

                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #2563eb, #3b82f6);
                    transition: width 0.5s ease;
                }

                .timeline-container {
                    position: relative;
                }

                .timeline-line {
                    position: absolute;
                    left: 30px;
                    top: 0;
                    bottom: 0;
                    width: 2px;
                    background: #e2e8f0;
                    z-index: 1;
                }

                .timeline-item {
                    display: flex;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    position: relative;
                }

                .timeline-item.completed .timeline-card {
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border-color: #3b82f6;
                }

                .timeline-node {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: #2563eb;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    z-index: 2;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
                    flex-shrink: 0;
                    margin-right: 2rem;
                }

                .timeline-card {
                    flex: 1;
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }

                .timeline-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    transform: translateY(-1px);
                }

                .card-header {
                    padding: 1.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 1rem;
                }

                .role-info {
                    flex: 1;
                }

                .role-title-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .role-info h3 {
                    font-size: 1.4rem;
                    font-weight: 600;
                    margin: 0;
                    color: #2563eb;
                }

                .badges {
                    display: flex;
                    gap: 0.5rem;
                }

                .priority-badge {
                    padding: 0.25rem 0.75rem;
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border-radius: 12px;
                    text-transform: uppercase;
                }

                .priority-alta { background: #dc2626; }
                .priority-media { background: #ea580c; }
                .priority-baja { background: #16a34a; }

                .time-badge {
                    padding: 0.25rem 0.75rem;
                    background: #f1f5f9;
                    color: #475569;
                    font-size: 0.75rem;
                    font-weight: 500;
                    border-radius: 12px;
                }

                .role-description {
                    color: #64748b;
                    margin: 0.5rem 0;
                    line-height: 1.5;
                }

                .query-count {
                    color: #3b82f6;
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .card-actions {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    align-items: flex-end;
                }

                .completion-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    border: 2px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .completion-btn:hover {
                    border-color: #3b82f6;
                    color: #3b82f6;
                }

                .completion-btn.completed {
                    background: #3b82f6;
                    border-color: #3b82f6;
                    color: white;
                }

                .expand-btn {
                    padding: 0.5rem;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .expand-btn:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                }

                .card-content {
                    padding: 0 1.5rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    animation: slideDown 0.3s ease;
                }

                .card-content h4 {
                    color: #374151;
                    margin: 1rem 0;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .queries-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .query-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 8px;
                    border-left: 4px solid #3b82f6;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    border: 1px solid transparent;
                }

                .query-item:hover {
                    background: #f1f5f9;
                    transform: translateX(4px);
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
                }

                .query-number {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #3b82f6;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    font-weight: 600;
                    flex-shrink: 0;
                }

                .query-item p {
                    margin: 0;
                    color: #374151;
                    line-height: 1.5;
                    flex: 1;
                }

                .query-arrow {
                    color: #3b82f6;
                    font-weight: bold;
                    font-size: 1.2rem;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 2rem;
                    animation: fadeIn 0.3s ease;
                }

                .modal-content {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    max-width: 1000px;
                    width: 100%;
                    max-height: 90vh;
                    overflow: hidden;
                    animation: slideUp 0.3s ease;
                }

                .modal-header {
                    padding: 2rem 2rem 1rem;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                }

                .modal-header h3 {
                    color: #1e293b;
                    margin: 0;
                    font-size: 1.5rem;
                    font-weight: 700;
                }

                .close-btn {
                    padding: 0.5rem;
                    border: none;
                    background: rgba(255, 255, 255, 0.8);
                    color: #64748b;
                    cursor: pointer;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-btn:hover {
                    background: #e2e8f0;
                    color: #374151;
                    transform: scale(1.05);
                }

                .modal-body {
                    padding: 2rem;
                    max-height: calc(90vh - 120px);
                    overflow-y: auto;
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 3rem;
                    text-align: center;
                }

                .animate-spin {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .agent-response-section {
                    background: linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%);
                    padding: 1.5rem;
                    border-radius: 12px;
                    margin-bottom: 2rem;
                    border-left: 4px solid #f59e0b;
                }

                .agent-response-section h4 {
                    margin: 0 0 1rem 0;
                    color: #92400e;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .agent-response-content {
                    color: #92400e;
                    line-height: 1.6;
                }

                .table-wrapper {
                    overflow-x: auto;
                    margin: 1rem 0;
                }

                .markdown-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.9rem;
                }

                .markdown-thead {
                    background-color: #f97316;
                    color: white;
                }

                .markdown-th {
                    padding: 0.75rem;
                    text-align: left;
                    font-weight: 600;
                    border: 1px solid #ea580c;
                }

                .markdown-tbody {
                    background-color: #fffbeb;
                }

                .markdown-tr:nth-child(even) {
                    background-color: #fef3c7;
                }

                .markdown-td {
                    padding: 0.75rem;
                    border: 1px solid #fde68a;
                    color: #78350f;
                }

                .visualization-section {
                    margin: 2rem 0;
                    text-align: center;
                }

                .visualize-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.3s ease;
                }

                .visualize-btn:hover:not(:disabled) {
                    background: #2563eb;
                    transform: translateY(-1px);
                }

                .visualize-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .visualization-results {
                    margin-top: 2rem;
                    border-top: 1px solid #e2e8f0;
                    padding-top: 2rem;
                }

                .error-message {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 1rem;
                    color: #dc2626;
                }

                .chart-container {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }

                .chart-container h4 {
                    margin: 0 0 1rem 0;
                    color: #374151;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .excel-download {
                    margin-top: 1rem;
                    text-align: center;
                }

                .excel-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: #16a34a;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 0.9rem;
                    transition: all 0.3s ease;
                }

                .excel-btn:hover {
                    background: #15803d;
                }

                .data-table {
                    margin-top: 2rem;
                }

                .data-table h4 {
                    margin: 0 0 1rem 0;
                    color: #374151;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (max-width: 768px) {
                    .roadmap-container {
                        padding: 0;
                    }
                    
                    .main-content {
                        padding: 1rem;
                    }
                    
                    .timeline-line {
                        left: 20px;
                    }
                    
                    .timeline-node {
                        width: 40px;
                        height: 40px;
                        margin-right: 1rem;
                    }
                    
                    .card-header {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    
                    .card-actions {
                        flex-direction: row;
                        width: 100%;
                        justify-content: space-between;
                    }
                    
                    .role-title-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }

                    .modal-overlay {
                        padding: 1rem;
                    }
                    
                    .modal-content {
                        max-height: 95vh;
                    }
                    
                    .modal-header {
                        padding: 1.5rem 1.5rem 1rem;
                    }
                    
                    .modal-body {
                        padding: 1.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default GuiaInteractiva;